import Breadcrumb from "components/Common/Breadcrumb"
import { useMemo, useState } from "react"
import { FaAlignLeft, FaBuilding, FaCalendarAlt, FaClipboardList, FaEdit, FaFileAlt, FaFileExcel, FaFlag, FaHistory, FaPaperclip, FaPlus, FaSave, FaSearch, FaSync, FaTasks, FaTimes, FaUsers } from "react-icons/fa"
import { MdCancel, MdDelete, MdNoteAdd } from "react-icons/md"
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  FormFeedback,
  Input,
  InputGroup,
  InputGroupText,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Table,
} from "reactstrap"
import React from "react"
import Swal from "sweetalert2"
import moment from "moment"
import Select from "react-select"
import { Switch } from "antd"
import * as XLSX from "xlsx"

// Design-first dummy statuses (TODO: replace with API "task-status" later)
const TASK_STATUSES = [
  { name: "To Do", color: "#6c757d" },
  { name: "In Progress", color: "#0d6efd" },
  { name: "On Hold", color: "#ffc107" },
  { name: "Completed", color: "#198754" },
  { name: "Cancelled", color: "#dc3545" },
]

// Design-first dummy employees (TODO: replace with API "users" later)
const DUMMY_EMPLOYEES = [
  { id: 1, name: "Anil Sharma" },
  { id: 2, name: "Priya Patel" },
  { id: 3, name: "Rahul Verma" },
  { id: 4, name: "Sneha Iyer" },
  { id: 5, name: "Amit Kumar" },
  { id: 6, name: "Neha Singh" },
]

const PRIORITIES = [
  { value: "High", color: "danger" },
  { value: "Medium", color: "warning" },
  { value: "Low", color: "success" },
]

const RECURRENCE_OPTIONS = ["Daily", "Weekly", "Monthly"]

// Design-first dummy departments (TODO: replace with API "departments" later)
const DUMMY_DEPARTMENTS = [
  { id: 1, departmentName: "Human Resources", employees: [1, 2] },
  { id: 2, departmentName: "Sales", employees: [3, 4, 5] },
  { id: 3, departmentName: "Accounts", employees: [6] },
]

// Design-first dummy data (TODO: replace with API "tasks" later)
const DUMMY_TASKS = [
  {
    id: 1,
    title: "Prepare monthly sales report",
    description: "Collect data from all regions and prepare the sales summary.",
    status: "In Progress",
    assignees: [1, 2],
    departmentId: 1,
    startDate: moment().subtract(2, "days").format("YYYY-MM-DD"),
    dueDate: moment().add(3, "days").format("YYYY-MM-DD"),
    priority: "High",
    recurring: false,
    recurrence: null,
    createdAt: new Date(),
  },
  {
    id: 2,
    title: "Client site visit - ABC Corp",
    description: "Visit client site for equipment installation review.",
    status: "To Do",
    assignees: [3],
    departmentId: 2,
    startDate: moment().format("YYYY-MM-DD"),
    dueDate: moment().add(5, "days").format("YYYY-MM-DD"),
    priority: "Medium",
    recurring: false,
    recurrence: null,
    createdAt: new Date(),
  },
  {
    id: 3,
    title: "Update staff attendance records",
    description: "Verify punch data and update missing entries for payroll.",
    status: "Completed",
    assignees: [4, 6],
    departmentId: null,
    startDate: moment().subtract(10, "days").format("YYYY-MM-DD"),
    dueDate: moment().subtract(3, "days").format("YYYY-MM-DD"),
    priority: "Low",
    recurring: false,
    recurrence: null,
    createdAt: new Date(),
  },
  {
    id: 4,
    title: "Follow up on pending quotations",
    description: "Call leads with quotations older than 7 days.",
    status: "On Hold",
    assignees: [5],
    departmentId: 2,
    startDate: moment().subtract(5, "days").format("YYYY-MM-DD"),
    dueDate: moment().subtract(1, "days").format("YYYY-MM-DD"),
    priority: "High",
    recurring: true,
    recurrence: "Weekly",
    createdAt: new Date(),
  },
]

const EMPTY_FORM = {
  title: "",
  description: "",
  status: "To Do",
  assignees: [],
  departmentId: null,
  startDate: moment().format("YYYY-MM-DD"),
  dueDate: "",
  priority: "Medium",
  recurring: false,
  recurrence: null,
}

// Design-first dummy task logs (TODO: replace with API "task-logs" later)
const DUMMY_TASK_LOGS = [
  {
    id: 1,
    taskId: 1,
    dateTime: moment().subtract(1, "days").toISOString(),
    status: "In Progress",
    description: "Started collecting region-wise sales data.",
    submittedBy: 1,
    attachment: null,
  },
  {
    id: 2,
    taskId: 1,
    dateTime: moment().subtract(5, "hours").toISOString(),
    status: "In Progress",
    description: "North region data compiled, working on south region now.",
    submittedBy: 2,
    attachment: { name: "north-region-sales.xlsx", size: "245 KB" },
  },
  {
    id: 3,
    taskId: 3,
    dateTime: moment().subtract(4, "days").toISOString(),
    status: "Completed",
    description: "All attendance records verified and updated for payroll.",
    submittedBy: 4,
    attachment: null,
  },
]

const getStatusColor = status =>
  TASK_STATUSES.find(s => s.name === status)?.color || "#6c757d"

const getPriorityColor = priority =>
  PRIORITIES.find(p => p.value === priority)?.color || "secondary"

const getInitials = name =>
  (name || "-")
    .split(" ")
    .map(part => part[0])
    .slice(0, 2)
    .join("")
    .toUpperCase()

const getNextOccurrenceText = (startDate, recurrence) => {
  if (!startDate || !recurrence) return null
  const date = moment(startDate, "YYYY-MM-DD")
  if (!date.isValid()) return null
  const today = moment().startOf("day")
  const next = date.clone().startOf("day")
  const stepOnce = () => {
    if (recurrence === "Daily") next.add(1, "day")
    else if (recurrence === "Weekly") next.add(1, "week")
    else {
      const day = next.date()
      next.date(1).add(1, "month")
      next.date(Math.min(day, next.daysInMonth()))
    }
  }
  // First run is one period after the start date, then roll forward to today
  stepOnce()
  let guard = 0
  while (next.isBefore(today) && guard < 500) {
    stepOnce()
    guard++
  }
  return next.format("ddd, DD MMM YYYY")
}

// Shared Add/Edit Task modal field styling (adapted from add-task-redesign.html)
const TASK_INPUT_STYLE = {
  height: 48,
  fontSize: 14,
  borderRadius: 11,
  borderColor: "#d8e1ef",
}
const TASK_FIELD_CARD_STYLE = {
  padding: 16,
  height: "100%",
  border: "1px solid #edf1f7",
  borderRadius: 15,
  background: "linear-gradient(145deg, #ffffff, #f8faff)",
}
const TASK_LABEL_ICON_STYLE = { size: 16, color: "#4b607f" }

// Solid backgrounds for the assignees dropdown (app CSS leaves it transparent)
const ASSIGNEE_SELECT_STYLES = {
  control: provided => ({
    ...provided,
    backgroundColor: "#ffffff",
    borderColor: "#d8e1ef",
    borderRadius: 11,
    minHeight: 48,
  }),
  menu: provided => ({
    ...provided,
    backgroundColor: "#ffffff",
  }),
  menuList: provided => ({
    ...provided,
    backgroundColor: "#ffffff",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? "#cb9019"
      : state.isFocused
        ? "#faf3e2"
        : "#ffffff",
    color: state.isSelected ? "#ffffff" : "#263a5b",
  }),
  multiValue: provided => ({
    ...provided,
    backgroundColor: "#faf3e2",
  }),
  multiValueLabel: provided => ({
    ...provided,
    color: "#6b5a33",
  }),
}

const TaskManagement = () => {
  document.title = "Task Management | Vintage Infra Equipment Sol PVT LTD"
  const [data, setData] = useState(DUMMY_TASKS)
  const [pageSize, setPageSize] = useState(20)
  const [searchString, setSearchString] = useState("")
  const [statusFilter, setStatusFilter] = useState("")
  const [priorityFilter, setPriorityFilter] = useState("")

  // Pagination
  const [currentPage, setCurrentPage] = useState(1)

  const [modalOpen, setModalOpen] = useState(false)
  const [selectedTask, setSelectedTask] = useState(EMPTY_FORM)
  const [errors, setErrors] = useState({})

  // Task logs (design-first local state)
  const [taskLogs, setTaskLogs] = useState(DUMMY_TASK_LOGS)
  const [logModalOpen, setLogModalOpen] = useState(false)
  const [logForm, setLogForm] = useState({
    id: null,
    taskId: null,
    dateTime: new Date().toISOString(),
    status: "To Do",
    description: "",
    submittedBy: null,
    attachment: null,
  })
  const [logErrors, setLogErrors] = useState({})
  const [historyTaskId, setHistoryTaskId] = useState(null)
  const [timelineTaskId, setTimelineTaskId] = useState(null)

  const employeeOptions = useMemo(
    () => DUMMY_EMPLOYEES.map(emp => ({ value: emp.id, label: emp.name })),
    []
  )

  const getEmployeeName = id =>
    DUMMY_EMPLOYEES.find(emp => emp.id === id)?.name || "-"

  const filteredData = useMemo(() => {
    const search = searchString.toLowerCase()
    return data.filter(item => {
      const matchesSearch =
        !search ||
        item?.title?.toLowerCase().includes(search) ||
        item?.description?.toLowerCase().includes(search) ||
        item?.assignees?.some(id =>
          getEmployeeName(id).toLowerCase().includes(search)
        )
      const matchesStatus = !statusFilter || item.status === statusFilter
      const matchesPriority = !priorityFilter || item.priority === priorityFilter
      return matchesSearch && matchesStatus && matchesPriority
    })
  }, [data, searchString, statusFilter, priorityFilter])

  const totalPages = Math.ceil(filteredData.length / pageSize) || 1
  const start = (currentPage - 1) * pageSize

  // Counts for the status pills (respect search + priority, ignore status)
  const statusCounts = useMemo(() => {
    const search = searchString.toLowerCase()
    const base = data.filter(item => {
      const matchesSearch =
        !search ||
        item?.title?.toLowerCase().includes(search) ||
        item?.description?.toLowerCase().includes(search) ||
        item?.assignees?.some(id =>
          getEmployeeName(id).toLowerCase().includes(search)
        )
      const matchesPriority =
        !priorityFilter || item.priority === priorityFilter
      return matchesSearch && matchesPriority
    })
    const counts = { "": base.length }
    TASK_STATUSES.forEach(s => {
      counts[s.name] = base.filter(t => t.status === s.name).length
    })
    return counts
  }, [data, searchString, priorityFilter])

  const paginatedData = useMemo(() => {
    const end = start + pageSize
    return filteredData.slice(start, end)
  }, [filteredData, currentPage, pageSize, start])

  const resetPage = () => setCurrentPage(1)

  const hasActiveFilters = searchString || statusFilter || priorityFilter

  const clearFilters = () => {
    setSearchString("")
    setStatusFilter("")
    setPriorityFilter("")
    setCurrentPage(1)
  }

  const getDepartmentName = id =>
    DUMMY_DEPARTMENTS.find(dept => dept.id === id)?.departmentName || "-"

  const exportToExcel = () => {
    const rows = filteredData.map((item, i) => ({
      "#": i + 1,
      Title: item.title || "",
      Description: item.description || "",
      Status: item.status || "",
      Assignees: (item.assignees || []).map(getEmployeeName).join(", "),
      Department: item.departmentId
        ? getDepartmentName(item.departmentId)
        : "-",
      "Start Date": item.startDate
        ? moment(item.startDate).format("DD-MM-YYYY")
        : "-",
      "Due Date": item.dueDate
        ? moment(item.dueDate).format("DD-MM-YYYY")
        : "-",
      Priority: item.priority || "",
      Recurring: item.recurring ? item.recurrence || "Yes" : "No",
      "Created At": item.createdAt
        ? moment(item.createdAt).format("DD-MM-YYYY")
        : "-",
    }))

    const ws = XLSX.utils.json_to_sheet(rows)
    ws["!cols"] = [
      { wch: 5 }, // #
      { wch: 35 }, // Title
      { wch: 45 }, // Description
      { wch: 14 }, // Status
      { wch: 30 }, // Assignees
      { wch: 20 }, // Department
      { wch: 13 }, // Start Date
      { wch: 13 }, // Due Date
      { wch: 11 }, // Priority
      { wch: 12 }, // Recurring
      { wch: 13 }, // Created At
    ]
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, "Tasks")
    XLSX.writeFile(wb, `Tasks_${moment().format("DD-MM-YYYY")}.xlsx`)
  }

  const openAddModal = () => {
    setSelectedTask({ ...EMPTY_FORM, startDate: moment().format("YYYY-MM-DD") })
    setErrors({})
    setModalOpen(true)
  }

  const openEditModal = item => {
    setSelectedTask({ ...item, assignees: [...(item.assignees || [])] })
    setErrors({})
    setModalOpen(true)
  }

  const handleDepartmentChange = e => {
    const departmentId = e.target.value ? Number(e.target.value) : null
    const department = DUMMY_DEPARTMENTS.find(d => d.id === departmentId)
    setSelectedTask(prev => ({
      ...prev,
      departmentId,
      assignees: department ? [...department.employees] : prev.assignees,
    }))
  }

  const validate = () => {
    const newErrors = {}
    if (!selectedTask?.title?.trim()) {
      newErrors.title = "Title is required"
    }
    if (!selectedTask?.status) {
      newErrors.status = "Status is required"
    }
    if (!selectedTask?.startDate) {
      newErrors.startDate = "Start date is required"
    }
    if (!selectedTask?.dueDate) {
      newErrors.dueDate = "Due date is required"
    }
    if (
      selectedTask?.startDate &&
      selectedTask?.dueDate &&
      moment(selectedTask.dueDate).isBefore(
        moment(selectedTask.startDate),
        "day"
      )
    ) {
      newErrors.dueDate = "Due date cannot be before start date"
    }
    if (selectedTask?.recurring && !selectedTask?.recurrence) {
      newErrors.recurrence = "Select recurrence frequency"
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Design-first: local CRUD only (no API yet)
  const handleSubmit = () => {
    if (!validate()) return

    if (selectedTask?.id) {
      setData(prev =>
        prev.map(item =>
          item.id === selectedTask.id
            ? { ...selectedTask, title: selectedTask.title.trim() }
            : item
        )
      )
      Swal.fire({
        title: "Task Updated Successfully.",
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      })
    } else {
      const newItem = {
        ...selectedTask,
        title: selectedTask.title.trim(),
        id: Date.now(),
        createdAt: new Date(),
      }
      setData(prev => [newItem, ...prev])
      Swal.fire({
        title: "Task Added Successfully.",
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      })
    }
    setModalOpen(false)
    setSelectedTask(EMPTY_FORM)
    setCurrentPage(1)
    // TODO: replace with FetchPostData / FetchPutData "tasks" when API is ready
  }

  const handleDelete = item => {
    Swal.fire({
      title: `Are you sure you want to delete "${item?.title}"?`,
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then(result => {
      if (result.isConfirmed) {
        setData(prev => prev.filter(d => d.id !== item.id))
        Swal.fire({
          title: "Task Deleted Successfully.",
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        })
        // TODO: replace with FetchDeleteData "tasks" when API is ready
      }
    })
  }

  const openAddLogModal = task => {
    setLogForm({
      id: null,
      taskId: task.id,
      dateTime: new Date().toISOString(),
      status: task.status,
      description: "",
      submittedBy: null,
      attachment: null,
    })
    setLogErrors({})
    setLogModalOpen(true)
  }

  const openEditLogModal = log => {
    setLogForm({ ...log })
    setLogErrors({})
    setLogModalOpen(true)
  }

  // Keep task status in sync with its latest log
  const syncTaskStatusToLatest = (taskId, logs) => {
    const latest = logs
      .filter(l => l.taskId === taskId)
      .sort((a, b) => new Date(b.dateTime) - new Date(a.dateTime))[0]
    if (latest) {
      setData(prev =>
        prev.map(t =>
          t.id === taskId ? { ...t, status: latest.status } : t
        )
      )
    }
  }

  const formatFileSize = bytes => {
    if (!bytes && bytes !== 0) return ""
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const handleLogFileChange = e => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > 10 * 1024 * 1024) {
      setLogErrors(prev => ({
        ...prev,
        attachment: "File must be under 10MB",
      }))
      return
    }
    setLogErrors(prev => ({ ...prev, attachment: null }))
    setLogForm(prev => ({
      ...prev,
      attachment: { name: file.name, size: formatFileSize(file.size), file },
    }))
  }

  const handleAttachmentDownload = attachment => {
    if (attachment?.file) {
      const url = URL.createObjectURL(attachment.file)
      const link = document.createElement("a")
      link.href = url
      link.download = attachment.name
      link.click()
      URL.revokeObjectURL(url)
    }
  }

  const handleLogSubmit = () => {
    const newErrors = {}
    if (!logForm.status) {
      newErrors.status = "Status is required"
    }
    if (!logForm.submittedBy) {
      newErrors.submittedBy = "Select who is submitting this log"
    }
    if (!logForm.description?.trim()) {
      newErrors.description = "Log description is required"
    }
    setLogErrors(newErrors)
    if (Object.keys(newErrors).length > 0) return

    if (logForm.id) {
      // Edit existing log (keep original date & time)
      const updatedLogs = taskLogs.map(l =>
        l.id === logForm.id
          ? {
              ...l,
              status: logForm.status,
              description: logForm.description.trim(),
              submittedBy: logForm.submittedBy,
              attachment: logForm.attachment,
            }
          : l
      )
      setTaskLogs(updatedLogs)
      syncTaskStatusToLatest(logForm.taskId, updatedLogs)
      Swal.fire({
        title: "Task Log Updated Successfully.",
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      })
    } else {
      const newLog = {
        id: Date.now(),
        taskId: logForm.taskId,
        dateTime: new Date().toISOString(),
        status: logForm.status,
        description: logForm.description.trim(),
        submittedBy: logForm.submittedBy,
        attachment: logForm.attachment,
      }
      const updatedLogs = [newLog, ...taskLogs]
      setTaskLogs(updatedLogs)
      syncTaskStatusToLatest(logForm.taskId, updatedLogs)
      Swal.fire({
        title: "Task Log Added Successfully.",
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      })
    }
    setLogModalOpen(false)
    // TODO: replace with FetchPostData / FetchPutData "task-logs" when API is ready
  }

  const handleDeleteLog = log => {
    Swal.fire({
      title: "Delete this log?",
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then(result => {
      if (result.isConfirmed) {
        const updatedLogs = taskLogs.filter(l => l.id !== log.id)
        setTaskLogs(updatedLogs)
        syncTaskStatusToLatest(log.taskId, updatedLogs)
        Swal.fire({
          title: "Task Log Deleted Successfully.",
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        })
        // TODO: replace with FetchDeleteData "task-logs" when API is ready
      }
    })
  }

  const taskLogsFor = taskId =>
    taskLogs
      .filter(log => log.taskId === taskId)
      .sort((a, b) => new Date(b.dateTime) - new Date(a.dateTime))

  const logTask = data.find(t => t.id === logForm.taskId)
  const nextOccurrence =
    selectedTask?.recurring && selectedTask?.recurrence
      ? getNextOccurrenceText(
          selectedTask.startDate,
          selectedTask.recurrence
        )
      : null
  const historyTask = data.find(t => t.id === historyTaskId)
  const historyLogs = historyTaskId ? taskLogsFor(historyTaskId) : []
  const timelineTask = data.find(t => t.id === timelineTaskId)
  const timelineLogs = timelineTaskId ? taskLogsFor(timelineTaskId) : []

  const isOverdue = item =>
    item.status !== "Completed" &&
    item.status !== "Cancelled" &&
    item.dueDate &&
    moment(item.dueDate).isBefore(moment(), "day")

  const renderAssignees = ids => {
    if (!ids?.length) return <span className="text-muted">Unassigned</span>
    const visible = ids.slice(0, 2)
    const remaining = ids.length - visible.length
    return (
      <div className="d-flex flex-wrap gap-1">
        {visible.map(id => (
          <Badge
            key={id}
            pill
            color="primary"
            className="fw-normal"
            style={{ fontSize: "0.85rem", padding: "0.4em 0.8em" }}
          >
            {getEmployeeName(id)}
          </Badge>
        ))}
        {remaining > 0 && (
          <Badge
            pill
            color="secondary"
            className="fw-normal"
            style={{ fontSize: "0.85rem", padding: "0.4em 0.8em" }}
          >
            +{remaining} more
          </Badge>
        )}
      </div>
    )
  }

  return (
    <div className="page-content">
      <Breadcrumb title="Dashboard" breadcrumbItem="Task Management" />

      <Card className="shadow-lg">
        <CardBody>
          {/* Toolbar */}
          <div className="bg-light rounded-4 p-3 mb-3">
            <Row className="g-2 align-items-end">
              <Col xs={12} lg>
                <Label className="small text-muted mb-1">Search</Label>
                <InputGroup>
                  <InputGroupText className="bg-white rounded-start-4">
                    <FaSearch className="text-muted" size={14} />
                  </InputGroupText>
                  <Input
                    className="rounded-end-4"
                    placeholder="Title, description or assignee..."
                    value={searchString}
                    onChange={e => {
                      setSearchString(e.target.value)
                      resetPage()
                    }}
                  />
                  {searchString && (
                    <Button
                      color="light"
                      className="rounded-4 ms-2 border"
                      title="Clear search"
                      onClick={() => {
                        setSearchString("")
                        resetPage()
                      }}
                    >
                      <FaTimes size={14} />
                    </Button>
                  )}
                </InputGroup>
              </Col>

              <Col xs={6} md={4} lg={2}>
                <Label className="small text-muted mb-1">Priority</Label>
                <Input
                  type="select"
                  value={priorityFilter}
                  onChange={e => {
                    setPriorityFilter(e.target.value)
                    resetPage()
                  }}
                  className="rounded-4"
                >
                  <option value="">All Priority</option>
                  {PRIORITIES.map(p => (
                    <option key={p.value} value={p.value}>
                      {p.value}
                    </option>
                  ))}
                </Input>
              </Col>

              <Col xs={6} md={4} lg={1}>
                <Label className="small text-muted mb-1">Show</Label>
                <Input
                  type="select"
                  value={pageSize}
                  onChange={e => {
                    setPageSize(Number(e.target.value)), setCurrentPage(1)
                  }}
                  className="rounded-4"
                >
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </Input>
              </Col>

              <Col xs={12} lg="auto">
                <div className="d-flex gap-2 justify-content-end">
                  <Button
                    color="success"
                    className="rounded-4"
                    onClick={exportToExcel}
                    disabled={filteredData.length === 0}
                    title="Export filtered tasks to Excel"
                  >
                    <FaFileExcel className="me-1" /> Export
                  </Button>
                  <Button
                    className="btn-blue rounded-4 px-3"
                    onClick={openAddModal}
                  >
                    <FaPlus className="me-2" />
                    Task
                  </Button>
                  {hasActiveFilters && (
                    <Button
                      color="light"
                      className="rounded-4 border"
                      title="Clear all filters"
                      onClick={clearFilters}
                    >
                      <FaTimes size={14} />
                    </Button>
                  )}
                </div>
              </Col>
            </Row>
          </div>

          {/* Status pills */}
          <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
            {[{ name: "", color: "#343a40" }, ...TASK_STATUSES].map(s => {
              const active = statusFilter === s.name
              const label = s.name === "" ? "All" : s.name
              const darkText = active && s.color === "#ffc107"
              return (
                <button
                  key={label}
                  type="button"
                  onClick={() => {
                    setStatusFilter(active ? "" : s.name)
                    resetPage()
                  }}
                  className="rounded-pill px-3 py-1"
                  style={{
                    border: `1px solid ${
                      active ? s.color : "#dee2e6"
                    }`,
                    backgroundColor: active ? s.color : "#fff",
                    color: darkText
                      ? "#212529"
                      : active
                        ? "#fff"
                        : "#495057",
                    fontSize: "0.85rem",
                    fontWeight: active ? 700 : 500,
                  }}
                >
                  {label}
                  <span
                    className="ms-2 rounded-pill px-2"
                    style={{
                      fontSize: "0.75rem",
                      backgroundColor: active
                        ? darkText
                          ? "rgba(0,0,0,.12)"
                          : "rgba(255,255,255,.25)"
                        : "#eef1f6",
                      color: darkText
                        ? "#212529"
                        : active
                          ? "#fff"
                          : "#495057",
                    }}
                  >
                    {statusCounts[s.name] ?? 0}
                  </span>
                </button>
              )
            })}
          </div>

          {/* Table */}
          <Table bordered responsive>
            <thead className="table-dark">
              <tr>
                <th style={{ width: "50px" }}>#</th>
                <th style={{ minWidth: "220px" }}>Task</th>
                <th>Status</th>
                <th>Assignees</th>
                <th>Start Date</th>
                <th>Due Date</th>
                <th>Priority</th>
                <th className="text-center" style={{ width: "190px" }}>
                  Action
                </th>
              </tr>
            </thead>

            <tbody>
              {paginatedData.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center">
                    No tasks found
                  </td>
                </tr>
              ) : (
                paginatedData.map((item, index) => (
                  <tr
                    key={item.id}
                    onClick={() => setTimelineTaskId(item.id)}
                    style={{ cursor: "pointer" }}
                    title="Click to view log timeline"
                    className={
                      item.status === "Completed"
                        ? "table-success"
                        : isOverdue(item)
                          ? "table-danger"
                          : ""
                    }
                  >
                    <td>{start + index + 1}</td>

                    <td>
                      <div className="fw-bold">
                        {item.title}
                        {item.recurring && (
                          <Badge
                            pill
                            color="info"
                            className="ms-2 fw-normal"
                            title={`Repeats ${item.recurrence}`}
                          >
                            <FaSync size={10} className="me-1" />
                            {item.recurrence}
                          </Badge>
                        )}
                      </div>
                      {item.description && (
                        <div
                          className="text-muted small text-truncate"
                          style={{ maxWidth: "280px" }}
                          title={item.description}
                        >
                          {item.description}
                        </div>
                      )}
                    </td>
                    <td>
                      <Badge
                        pill
                        style={{
                          backgroundColor: getStatusColor(item.status),
                          fontSize: "0.78rem",
                          padding: "0.4em 0.9em",
                        }}
                      >
                        {item.status}
                      </Badge>
                    </td>
                    <td>{renderAssignees(item.assignees)}</td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      {item.startDate
                        ? moment(item.startDate).format("DD-MM-YYYY")
                        : "-"}
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      {item.dueDate
                        ? moment(item.dueDate).format("DD-MM-YYYY")
                        : "-"}
                    </td>
                    <td>
                      <Badge
                        pill
                        color={getPriorityColor(item.priority)}
                        style={{ fontSize: "0.85rem", padding: "0.4em 0.9em" }}
                      >
                        {item.priority}
                      </Badge>
                    </td>

                    <td
                      className="text-center"
                      style={{ whiteSpace: "nowrap" }}
                      onClick={e => e.stopPropagation()}
                    >
                      <Button
                        className="bg-info text-white me-2 border-0 shadow-none"
                        title="Add Task Log"
                        onClick={() => openAddLogModal(item)}
                      >
                        <MdNoteAdd size={18} />
                      </Button>

                      <Button
                        className="bg-secondary text-white me-2 border-0 shadow-none"
                        title="View Log History"
                        onClick={() => setHistoryTaskId(item.id)}
                      >
                        <FaHistory size={15} />
                      </Button>

                      <Button
                        className="bg-success text-white me-2 border-0 shadow-none"
                        title="Edit Task"
                        onClick={() => openEditModal(item)}
                      >
                        <FaEdit size={16} />
                      </Button>

                      <Button
                        className="bg-danger text-white border-0 shadow-none"
                        title="Delete Task"
                        onClick={() => handleDelete(item)}
                      >
                        <MdDelete size={18} />
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
          <nav
            aria-label="Page navigation example"
            className="d-flex justify-content-between mt-3"
          >
            <h5>
              Showing {filteredData.length === 0 ? 0 : start + 1} to{" "}
              {Math.min(start + pageSize, filteredData.length)} of{" "}
              {filteredData.length} entries
            </h5>
            <ul className="pagination mb-0">
              <li
                className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                >
                  Previous
                </button>
              </li>

              {[...Array(totalPages)].map((_, i) => {
                const pageNum = i + 1
                return (
                  <li
                    key={pageNum}
                    className={`page-item ${
                      pageNum === currentPage ? "active" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage(pageNum)}
                    >
                      {pageNum}
                    </button>
                  </li>
                )
              })}

              <li
                className={`page-item ${
                  currentPage === totalPages ? "disabled" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() =>
                    setCurrentPage(prev => Math.min(prev + 1, totalPages))
                  }
                >
                  Next
                </button>
              </li>
            </ul>
          </nav>
        </CardBody>
      </Card>

      <Modal
        isOpen={modalOpen}
        toggle={() => setModalOpen(false)}
        centered
        fullscreen
      >
        <div
          className="d-flex align-items-center gap-3 px-4 py-3 text-white"
          style={{
            background: "linear-gradient(120deg, #8a6414, #cb9019)",
          }}
        >
          <div
            className="d-flex align-items-center justify-content-center flex-shrink-0"
            style={{
              width: 52,
              height: 52,
              borderRadius: 12,
              background: "linear-gradient(145deg, #e3b23c, #a57612)",
            }}
          >
            <FaTasks size={26} />
          </div>
          <div className="flex-grow-1" style={{ minWidth: 0 }}>
            <h5 className="mb-0 fw-bold">
              {selectedTask?.id ? "Edit" : "Add"} Task
            </h5>
            <small style={{ color: "#f7ecd4" }}>
              {selectedTask?.id
                ? "Update the task details below."
                : "Create a new task and keep your work on track."}
            </small>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={() => setModalOpen(false)}
            className="btn btn-link text-decoration-none p-0"
            style={{ color: "#e8eef9", fontSize: 28, lineHeight: 1 }}
          >
            <FaTimes size={20} />
          </button>
        </div>
        <ModalBody style={{ padding: "26px 28px 20px" }}>
          <Row>
            <Col md={12} className="mb-3">
              <Label className="fw-bold" style={{ fontSize: 14 }}>
                Title <span className="text-danger">*</span>
              </Label>
              <div style={{ position: "relative" }}>
                <span
                  style={{
                    position: "absolute",
                    left: 15,
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#72829c",
                  }}
                >
                  <FaFileAlt size={17} />
                </span>
                <Input
                  placeholder="e.g. Prepare monthly sales report"
                  value={selectedTask?.title || ""}
                  invalid={!!errors.title}
                  onChange={e =>
                    setSelectedTask(prev => ({ ...prev, title: e.target.value }))
                  }
                  style={{ ...TASK_INPUT_STYLE, height: 52, paddingLeft: 45 }}
                />
              </div>
              {errors.title && (
                <FormFeedback className="d-block">{errors.title}</FormFeedback>
              )}
            </Col>

            <Col md={3} className="mb-3">
              <div style={TASK_FIELD_CARD_STYLE}>
                <Label
                  className="fw-bold d-flex align-items-center gap-2"
                  style={{ fontSize: 14 }}
                >
                  <span
                    style={{
                      display: "inline-block",
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      backgroundColor: getStatusColor(
                        selectedTask?.status || "To Do"
                      ),
                      boxShadow: `0 0 0 3px ${getStatusColor(
                        selectedTask?.status || "To Do"
                      )}2e`,
                    }}
                  />
                  Status <span className="text-danger">*</span>
                </Label>
                <Input
                  type="select"
                  value={selectedTask?.status || "To Do"}
                  invalid={!!errors.status}
                  onChange={e =>
                    setSelectedTask(prev => ({ ...prev, status: e.target.value }))
                  }
                  style={{
                    ...TASK_INPUT_STYLE,
                    borderColor: "#ffe0a1",
                    backgroundColor: "#fffaf0",
                  }}
                >
                  {TASK_STATUSES.map(s => (
                    <option key={s.name} value={s.name}>
                      {s.name}
                    </option>
                  ))}
                </Input>
                {errors.status && (
                  <FormFeedback className="d-block">
                    {errors.status}
                  </FormFeedback>
                )}
              </div>
            </Col>

            <Col md={3} className="mb-3">
              <div style={TASK_FIELD_CARD_STYLE}>
                <Label
                  className="fw-bold d-flex align-items-center gap-2"
                  style={{ fontSize: 14 }}
                >
                  <FaFlag size={TASK_LABEL_ICON_STYLE.size} color={TASK_LABEL_ICON_STYLE.color} />
                  Priority
                </Label>
                <Input
                  type="select"
                  value={selectedTask?.priority || "Medium"}
                  onChange={e =>
                    setSelectedTask(prev => ({
                      ...prev,
                      priority: e.target.value,
                    }))
                  }
                  style={{
                    ...TASK_INPUT_STYLE,
                    borderColor: "#ecd9a8",
                    backgroundColor: "#fdf8ec",
                    fontWeight: 600,
                  }}
                >
                  {PRIORITIES.map(p => (
                    <option key={p.value} value={p.value}>
                      {p.value}
                    </option>
                  ))}
                </Input>
              </div>
            </Col>

            <Col md={3} className="mb-3">
              <div style={TASK_FIELD_CARD_STYLE}>
                <Label
                  className="fw-bold d-flex align-items-center gap-2"
                  style={{ fontSize: 14 }}
                >
                  <FaCalendarAlt size={TASK_LABEL_ICON_STYLE.size} color={TASK_LABEL_ICON_STYLE.color} />
                  Start Date <span className="text-danger">*</span>
                </Label>
                <Input
                  type="date"
                  value={selectedTask?.startDate || ""}
                  invalid={!!errors.startDate}
                  onChange={e =>
                    setSelectedTask(prev => ({
                      ...prev,
                      startDate: e.target.value,
                    }))
                  }
                  style={TASK_INPUT_STYLE}
                />
                {errors.startDate && (
                  <FormFeedback className="d-block">
                    {errors.startDate}
                  </FormFeedback>
                )}
              </div>
            </Col>

            <Col md={3} className="mb-3">
              <div style={TASK_FIELD_CARD_STYLE}>
                <Label
                  className="fw-bold d-flex align-items-center gap-2"
                  style={{ fontSize: 14 }}
                >
                  <FaCalendarAlt size={TASK_LABEL_ICON_STYLE.size} color={TASK_LABEL_ICON_STYLE.color} />
                  Due Date <span className="text-danger">*</span>
                </Label>
                <Input
                  type="date"
                  min={selectedTask?.startDate || ""}
                  value={selectedTask?.dueDate || ""}
                  invalid={!!errors.dueDate}
                  onChange={e =>
                    setSelectedTask(prev => ({
                      ...prev,
                      dueDate: e.target.value,
                    }))
                  }
                  style={TASK_INPUT_STYLE}
                />
                {errors.dueDate && (
                  <FormFeedback className="d-block">
                    {errors.dueDate}
                  </FormFeedback>
                )}
              </div>
            </Col>

            <Col md={4} className="mb-3">
              <Label
                className="fw-bold d-flex align-items-center gap-2"
                style={{ fontSize: 14 }}
              >
                <FaBuilding size={TASK_LABEL_ICON_STYLE.size} color={TASK_LABEL_ICON_STYLE.color} />
                Department
              </Label>
              <Input
                type="select"
                value={selectedTask?.departmentId || ""}
                onChange={handleDepartmentChange}
                style={TASK_INPUT_STYLE}
              >
                <option value="">Select department</option>
                {DUMMY_DEPARTMENTS.map(dept => (
                  <option key={dept.id} value={dept.id}>
                    {dept.departmentName}
                  </option>
                ))}
              </Input>
            </Col>

            <Col md={8} className="mb-3">
              <Label
                className="fw-bold d-flex align-items-center gap-2"
                style={{ fontSize: 14 }}
              >
                <FaUsers size={TASK_LABEL_ICON_STYLE.size} color={TASK_LABEL_ICON_STYLE.color} />
                Assignees
              </Label>
              <Select
                isMulti
                options={employeeOptions}
                placeholder="Select assignees"
                styles={ASSIGNEE_SELECT_STYLES}
                value={employeeOptions.filter(opt =>
                  selectedTask?.assignees?.includes(opt.value)
                )}
                onChange={selected =>
                  setSelectedTask(prev => ({
                    ...prev,
                    assignees: (selected || []).map(opt => opt.value),
                  }))
                }
              />
            </Col>

            <Col md={12} className="mb-3">
              <div
                style={{
                  padding: "16px 18px",
                  border: "1px solid #efe0bd",
                  borderRadius: 15,
                  background:
                    "linear-gradient(105deg, #fdf9ef, #faf3e0)",
                }}
              >
                <div className="d-flex align-items-center justify-content-between">
                  <div
                    className="d-flex align-items-center gap-2 fw-bold"
                    style={{ fontSize: 14 }}
                  >
                    <FaSync size={18} color="#ad7e1f" />
                    Recurring
                  </div>
                  <Switch
                    checked={selectedTask?.recurring ?? false}
                    onChange={checked =>
                      setSelectedTask(prev => ({
                        ...prev,
                        recurring: checked,
                        recurrence: checked ? prev.recurrence : null,
                      }))
                    }
                  />
                </div>
                {selectedTask?.recurring && (
                  <div className="d-flex align-items-center flex-wrap mt-3">
                    {RECURRENCE_OPTIONS.map(opt => {
                      const active = selectedTask?.recurrence === opt
                      return (
                        <button
                          key={opt}
                          type="button"
                          aria-pressed={active}
                          onClick={() =>
                            setSelectedTask(prev => ({
                              ...prev,
                              recurrence: opt,
                            }))
                          }
                          className="d-inline-flex align-items-center me-2 mb-2"
                          style={{
                            gap: 9,
                            minHeight: 40,
                            padding: "0 15px",
                            border: active
                              ? "1px solid #cb9019"
                              : "1px solid #e3d3a8",
                            borderRadius: 999,
                            color: active ? "#fff" : "#6b5a33",
                            background: active
                              ? "linear-gradient(135deg, #d9a62e, #ad7e1f)"
                              : "#fff",
                            fontSize: 14,
                            fontWeight: 650,
                            boxShadow: active
                              ? "0 4px 10px rgba(203,144,25,.25)"
                              : "none",
                          }}
                        >
                          <span
                            style={{
                              width: 17,
                              height: 17,
                              borderRadius: "50%",
                              border: active
                                ? "2px solid #fff"
                                : "2px solid #d3c096",
                              background: active ? "#f3e3bb" : "#fff",
                              boxShadow: active
                                ? "inset 0 0 0 4px #fff"
                                : "none",
                            }}
                          />
                          {opt}
                        </button>
                      )
                    })}
                    <span
                      className="d-inline-flex align-items-center mb-2 ms-2"
                      style={{ gap: 8, color: "#7b8ba5", fontSize: 12 }}
                    >
                      <FaCalendarAlt size={15} color="#ad7e1f" />
                      {nextOccurrence
                        ? `Next on ${nextOccurrence}`
                        : "Select a start date to calculate the next occurrence."}
                    </span>
                  </div>
                )}
                {errors.recurrence && (
                  <div className="text-danger small mt-1">
                    {errors.recurrence}
                  </div>
                )}
              </div>
            </Col>

            <Col md={12}>
              <Label
                className="fw-bold d-flex align-items-center gap-2"
                style={{ fontSize: 14 }}
              >
                <FaAlignLeft size={TASK_LABEL_ICON_STYLE.size} color={TASK_LABEL_ICON_STYLE.color} />
                Description
              </Label>
              <Input
                type="textarea"
                rows={2}
                placeholder="Task details (optional)"
                value={selectedTask?.description || ""}
                onChange={e =>
                  setSelectedTask(prev => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                style={{
                  minHeight: 76,
                  padding: "13px 15px",
                  lineHeight: 1.5,
                  fontSize: 14,
                  borderRadius: 11,
                  borderColor: "#d8e1ef",
                }}
              />
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter
          className="d-flex justify-content-end gap-2"
          style={{
            padding: "16px 28px 22px",
            borderTop: "1px solid #edf1f7",
          }}
        >
          <Button
            onClick={handleSubmit}
            style={{
              minWidth: 112,
              height: 46,
              borderRadius: 10,
              fontWeight: 700,
              fontSize: 14,
              color: "#243956",
              borderColor: "#c9d7eb",
              backgroundColor: "#f5f8fd",
              borderWidth: 1,
            }}
          >
            <FaSave className="me-2" /> Save
          </Button>
          <Button
            className="btn-danger"
            onClick={() => setModalOpen(false)}
            style={{
              minWidth: 112,
              height: 46,
              borderRadius: 10,
              fontWeight: 700,
              fontSize: 14,
            }}
          >
            <MdCancel className="me-2" /> Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {/* Add / Edit Task Log Modal */}
      <Modal
        isOpen={logModalOpen}
        toggle={() => setLogModalOpen(false)}
        centered
        size="lg"
      >
        <ModalHeader toggle={() => setLogModalOpen(false)}>
          {logForm.id ? "Edit" : "Add"} Task Log
          {logTask && (
            <div className="small text-muted fw-normal">{logTask.title}</div>
          )}
        </ModalHeader>
        <ModalBody>
          <Row>
            <Col md={6} className="mb-3">
              <Label>Date & Time</Label>
              <Input
                disabled
                value={
                  logForm.dateTime
                    ? moment(logForm.dateTime).format("DD-MM-YYYY | hh:mm A")
                    : ""
                }
              />
            </Col>

            <Col md={6} className="mb-3">
              <Label>
                Submitted By <span className="text-danger">*</span>
              </Label>
              <Input
                type="select"
                value={logForm.submittedBy || ""}
                invalid={!!logErrors.submittedBy}
                onChange={e =>
                  setLogForm(prev => ({
                    ...prev,
                    submittedBy: e.target.value ? Number(e.target.value) : null,
                  }))
                }
              >
                <option value="">Select employee</option>
                {(logTask?.assignees?.length
                  ? logTask.assignees
                  : DUMMY_EMPLOYEES.map(emp => emp.id)
                ).map(id => (
                  <option key={id} value={id}>
                    {getEmployeeName(id)}
                  </option>
                ))}
              </Input>
              {logErrors.submittedBy && (
                <FormFeedback className="d-block">
                  {logErrors.submittedBy}
                </FormFeedback>
              )}
            </Col>

            <Col md={6} className="mb-3">
              <Label>
                Status <span className="text-danger">*</span>
              </Label>
              <Input
                type="select"
                value={logForm.status}
                invalid={!!logErrors.status}
                onChange={e =>
                  setLogForm(prev => ({ ...prev, status: e.target.value }))
                }
              >
                {TASK_STATUSES.map(s => (
                  <option key={s.name} value={s.name}>
                    {s.name}
                  </option>
                ))}
              </Input>
              {logErrors.status && (
                <FormFeedback className="d-block">
                  {logErrors.status}
                </FormFeedback>
              )}
            </Col>

            <Col md={6} className="mb-3">
              <Label>Attachment</Label>
              <Input
                type="file"
                onChange={handleLogFileChange}
                className={
                  logErrors.attachment ? "is-invalid rounded-4" : "rounded-4"
                }
              />
              {logErrors.attachment && (
                <div className="invalid-feedback d-block">
                  {logErrors.attachment}
                </div>
              )}
              {logForm.attachment && (
                <div className="d-flex align-items-center gap-2 mt-2 border rounded-4 px-3 py-2">
                  <FaPaperclip size={13} className="text-muted" />
                  <span className="small flex-grow-1 text-truncate">
                    {logForm.attachment.name}
                    {logForm.attachment.size &&
                      ` (${logForm.attachment.size})`}
                  </span>
                  <Button
                    size="sm"
                    color="link"
                    className="text-danger p-0 text-decoration-none"
                    title="Remove attachment"
                    onClick={() =>
                      setLogForm(prev => ({ ...prev, attachment: null }))
                    }
                  >
                    <FaTimes size={13} />
                  </Button>
                </div>
              )}
            </Col>

            <Col md={12}>
              <Label>
                Log Description <span className="text-danger">*</span>
              </Label>
              <Input
                type="textarea"
                rows={3}
                placeholder="What progress was made?"
                value={logForm.description}
                invalid={!!logErrors.description}
                onChange={e =>
                  setLogForm(prev => ({ ...prev, description: e.target.value }))
                }
              />
              {logErrors.description && (
                <FormFeedback className="d-block">
                  {logErrors.description}
                </FormFeedback>
              )}
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button className="btn-blue" onClick={handleLogSubmit}>
            <FaSave /> Save
          </Button>
          <Button
            className="btn-danger"
            onClick={() => setLogModalOpen(false)}
          >
            <MdCancel /> Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {/* Task Log History Modal */}
      <Modal
        isOpen={!!historyTaskId}
        toggle={() => setHistoryTaskId(null)}
        centered
        fullscreen
      >
        <ModalHeader toggle={() => setHistoryTaskId(null)}>
          Task Log History
          {historyTask && (
            <div className="small text-muted fw-normal">
              {historyTask.title}
            </div>
          )}
        </ModalHeader>
        <ModalBody style={{ maxHeight: "60vh", overflowY: "auto" }}>
          {historyLogs.length === 0 ? (
            <p className="text-center text-muted mb-0">
              No logs added for this task yet.
            </p>
          ) : (
            <Table bordered responsive className="mb-0">
              <thead className="table-dark">
                <tr>
                  <th style={{ width: "50px" }}>#</th>
                  <th style={{ whiteSpace: "nowrap" }}>Date & Time</th>
                  <th>Submitted By</th>
                  <th>Status</th>
                  <th style={{ minWidth: "200px" }}>Description</th>
                  <th>Attachment</th>
                  <th className="text-center" style={{ width: "110px" }}>
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {historyLogs.map((log, index) => (
                  <tr key={log.id}>
                    <td>{index + 1}</td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      {moment(log.dateTime).format("DD-MM-YYYY | hh:mm A")}
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      {getEmployeeName(log.submittedBy)}
                    </td>
                    <td>
                      <Badge
                        pill
                        style={{
                          backgroundColor: getStatusColor(log.status),
                          fontSize: "0.75rem",
                          padding: "0.35em 0.8em",
                        }}
                      >
                        {log.status}
                      </Badge>
                    </td>
                    <td>{log.description}</td>
                    <td>
                      {log.attachment ? (
                        <button
                          type="button"
                          className={`btn btn-link btn-sm p-0 text-decoration-none ${
                            log.attachment.file ? "" : "text-muted"
                          }`}
                          title={
                            log.attachment.file
                              ? "Download attachment"
                              : log.attachment.name
                          }
                          onClick={() =>
                            handleAttachmentDownload(log.attachment)
                          }
                          style={{
                            fontSize: "0.8rem",
                            whiteSpace: "nowrap",
                          }}
                        >
                          <FaPaperclip size={12} className="me-1" />
                          {log.attachment.name}
                          {log.attachment.size &&
                            ` (${log.attachment.size})`}
                        </button>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td
                      className="text-center"
                      style={{ whiteSpace: "nowrap" }}
                    >
                      <Button
                        size="sm"
                        className="bg-success text-white me-2 border-0 shadow-none"
                        title="Edit log"
                        onClick={() => openEditLogModal(log)}
                      >
                        <FaEdit size={12} />
                      </Button>
                      <Button
                        size="sm"
                        className="bg-danger text-white border-0 shadow-none"
                        title="Delete log"
                        onClick={() => handleDeleteLog(log)}
                      >
                        <MdDelete size={14} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </ModalBody>
      </Modal>

      {/* Log Timeline (read-only) Modal */}
      <Modal
        isOpen={!!timelineTaskId}
        toggle={() => setTimelineTaskId(null)}
        centered
        size="lg"
      >
        <ModalHeader toggle={() => setTimelineTaskId(null)}>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <span>Log Timeline</span>
            {timelineTask && (
              <Badge
                pill
                style={{
                  backgroundColor: getStatusColor(timelineTask.status),
                  fontSize: "0.72rem",
                  padding: "0.35em 0.8em",
                }}
              >
                {timelineTask.status}
              </Badge>
            )}
          </div>
          {timelineTask && (
            <div className="small text-muted fw-normal">
              {timelineTask.title} · {timelineLogs.length}{" "}
              {timelineLogs.length === 1 ? "update" : "updates"}
            </div>
          )}
        </ModalHeader>
        <ModalBody
          className="bg-light"
          style={{ maxHeight: "60vh", overflowY: "auto" }}
        >
          {timelineLogs.length === 0 ? (
            <div className="text-center py-5">
              <FaClipboardList size={44} className="text-muted mb-3" />
              <h6 className="fw-bold">No updates yet</h6>
              <p className="text-muted small mb-0">
                Logs added by assignees will appear here.
              </p>
            </div>
          ) : (
            timelineLogs.map((log, index) => {
              const color = getStatusColor(log.status)
              const isLast = index === timelineLogs.length - 1
              return (
                <div key={log.id} className={`d-flex ${isLast ? "" : "pb-1"}`}>
                  <div className="d-flex flex-column align-items-center me-3">
                    <div
                      className="rounded-circle flex-shrink-0 mt-1"
                      style={{
                        width: "15px",
                        height: "15px",
                        backgroundColor: color,
                        boxShadow: `0 0 0 4px ${color}26`,
                      }}
                    />
                    {!isLast && (
                      <div
                        className="flex-grow-1"
                        style={{
                          width: "2px",
                          minHeight: "28px",
                          backgroundColor: "#dee2e6",
                        }}
                      />
                    )}
                  </div>
                  <div
                    className={`flex-grow-1 bg-white border rounded-3 p-3 ${
                      isLast ? "" : "mb-3"
                    }`}
                  >
                    <div className="d-flex justify-content-between align-items-center gap-2 flex-wrap mb-2">
                      <Badge
                        pill
                        style={{
                          backgroundColor: color,
                          fontSize: "0.75rem",
                          padding: "0.35em 0.85em",
                        }}
                      >
                        {log.status}
                      </Badge>
                      <small
                        className="text-muted"
                        title={moment(log.dateTime).format(
                          "DD-MM-YYYY | hh:mm A"
                        )}
                      >
                        {moment(log.dateTime).fromNow()}
                      </small>
                    </div>
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <span
                        className="rounded-circle d-inline-flex align-items-center justify-content-center fw-bold flex-shrink-0"
                        style={{
                          width: "32px",
                          height: "32px",
                          fontSize: "0.75rem",
                          backgroundColor: color,
                          color:
                            color === "#ffc107" ? "#212529" : "#fff",
                        }}
                      >
                        {getInitials(getEmployeeName(log.submittedBy))}
                      </span>
                      <div>
                        <div className="fw-bold" style={{ fontSize: "0.9rem" }}>
                          {getEmployeeName(log.submittedBy)}
                        </div>
                        <small className="text-muted">
                          {moment(log.dateTime).format("DD-MM-YYYY | hh:mm A")}
                        </small>
                      </div>
                    </div>
                    <p className="mb-0">{log.description}</p>
                    {log.attachment && (
                      <button
                        type="button"
                        className="btn btn-light btn-sm mt-2 border rounded-pill px-3 text-decoration-none"
                        title={
                          log.attachment.file
                            ? "Download attachment"
                            : log.attachment.name
                        }
                        onClick={() =>
                          handleAttachmentDownload(log.attachment)
                        }
                        style={{ fontSize: "0.8rem" }}
                      >
                        <FaPaperclip size={12} className="me-1" />
                        {log.attachment.name}
                        {log.attachment.size &&
                          ` (${log.attachment.size})`}
                      </button>
                    )}
                  </div>
                </div>
              )
            })
          )}
        </ModalBody>
      </Modal>
    </div>
  )
}

export default TaskManagement
